import { Form, Formik } from 'formik';
import Input from '@/components/ui/inputFormik';
import AskToggle from '@/components/ui/buttons/ask-toggle';
import InputPassword from '@/components/ui/inputFormik/input-password';
import { registerEmailValidation } from '@/utils/validation/auth';
import { toast } from 'sonner';
import { useLocale } from 'use-intl';
import { registerCreate } from '@/services/auth';
import InputMobile from '@/components/ui/inputFormik/input-mobile';
import AuthStatment from '../auth-statment';
import { event_user_auth } from '@/utils/gtm';
import NextLink from '@/components/next-link';
import { useTurnstile } from 'react-turnstile';
import TurnstileRecaptcha from '@/components/turnstile/TurnstileRecaptcha';

const Register = ({ setAuth, t }) => {
  const locale = useLocale();
  const turnstile = useTurnstile();
  const handleRegister = async (
    values,
    { setSubmitting, setFieldError, setFieldValue }
  ) => {
    const submittedData = {
      password: values?.password,
      firstname: values?.firstname,
      lastname: values?.lastname,
      email: values?.email,
      mobile: `00966${values?.mobile.slice(1)}`,
      registerType: 'mobile',
    };

    const res = await registerCreate(submittedData, values?.recaptchaToken);

    if (!res?.data?.data && res.status !== 200) {
      const errorMsg = res?.error?.error?.message || '';
      const keysErrorFields = {
        email: 'email',
        Email: 'email',
        mobile: 'mobile',
        'First Name': 'firstname',
        'Last Name': 'lastname',
      };
      turnstile.reset();
      setFieldValue('recaptchaToken', '');

      // Find the first matching key in the error message
      const matchedKey = Object.keys(keysErrorFields)?.find((key) =>
        errorMsg?.toLowerCase()?.includes(key?.toLowerCase())
      );

      if (matchedKey) {
        setFieldError(keysErrorFields?.[matchedKey], errorMsg);
      } else {
        // Default field if no match found
        setFieldError('confirmPassword', errorMsg);
      }
    } else {
      toast.success(
        locale === 'en' ? 'You registered successfully' : 'تم التسجيل بنجاح'
      );
      event_user_auth(
        {
          firstname: values?.firstname,
          lastname: values?.lastname,
          email: values?.email,
          mobilenumber: `00966${values?.mobile.slice(1)}`,
          id: res?.data?.data?.user?.id,
        },
        'sign_up',
        'register'
      );
      setAuth('login-email');
    }

    setSubmitting(false);
  };

  return (
    <>
      <div className='max-md:py-0 md:pt-1 md:pb-5'>
        <AuthStatment
          title={t('If you have an account,')}
          description={t('Log in using your email address')}
        />
      </div>
      <Formik
        onSubmit={handleRegister}
        validationSchema={registerEmailValidation(t)}
        initialValues={{
          password: '',
          confirmPassword: '',
          firstname: '',
          lastname: '',
          email: '',
          mobile: '',
          acceptedTerms: false,
          recaptchaToken: '',
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className='grid grid-cols-1 gap-2 px-2 lg:grid-cols-2 lg:gap-4 lg:px-8'>
            <Input
              mandatory={true}
              name='firstname'
              id='first-name'
              title={t('First Name')}
            />
            <Input
              mandatory={true}
              name='lastname'
              id='last-name'
              title={t('Last Name')}
            />
            <div className='col-span-full'>
              <Input
                labelClassName='max-sm:text-xs'
                inputClassName='max-h-7.5 lg:min-h-[38px]'
                name='email'
                id='email'
                title={t('Email')}
                mandatory={true}
              />
            </div>
            <div className='col-span-full'>
              <InputMobile
                mandatory={true}
                name='mobile'
                labelClassName='max-sm:text-xs'
                id='mobile'
                title={t('Phone Number')}
              />
            </div>
            <div className='col-span-full'>
              <InputPassword
                iconClassName='max-sm:size-4'
                mandatory={true}
                name='password'
                labelClassName='max-sm:text-xs'
                inputClassName='max-sm:h-2.5 max-sm:py-0'
                id='password'
                title={t('Password')}
              />
            </div>
            <div className='col-span-full'>
              <InputPassword
                iconClassName='max-sm:size-4'
                labelClassName='max-sm:text-xs'
                inputClassName='max-sm:h-2.5 max-sm:py-0'
                mandatory={true}
                name='confirmPassword'
                id='confirmPassword'
                title={t('Confirm Password')}
              />
            </div>
            <div className='col-span-full flex items-center gap-1.5 lg:gap-3'>
              <Input
                mandatory={true}
                type='checkbox'
                name='acceptedTerms'
                title={
                  <span className='max-lg:text-[15px] max-md:text-sm max-sm:text-xs lg:text-lg'>
                    {t('I Agree to')}{' '}
                    <NextLink
                      href='/static/terms-and-conditions'
                      target='_blank'
                      className='underline'
                    >
                      {t('Terms and Conditions')}
                    </NextLink>
                  </span>
                }
              />
            </div>
            <div className='col-span-full'>
              <TurnstileRecaptcha
                action='register'
                className='flex items-center justify-center p-2'
                onVerify={(value) => {
                  setFieldValue('recaptchaToken', value);
                }}
              />
            </div>
            <button
              type='submit'
              disabled={isSubmitting}
              className='col-span-full flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg bg-red-600 py-1 font-bold text-white hover:bg-red-600/70 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600 max-lg:text-sm max-sm:h-9 lg:h-10 lg:py-1.5'
            >
              {isSubmitting && (
                <span className='border-primary-500 size-4 animate-spin rounded-full border-t border-r py-1 max-lg:size-2' />
              )}
              {t('Register')}
            </button>
            <AskToggle fromRegister t={t} setAuth={setAuth} />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default Register;
